import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Checkbox from "../components/Checkbox";
import "./TermsAndConditions.scss";

const TermsAndConditions = ({
  question,
  values,
  errors,
  setFieldValue,
  setFieldTouched,
}) => {
  const { t } = useTranslation(["survey"]);
  const [expandedTerms, setExpandedTerms] = useState({});

  const handleCheckboxChange = (termIndex, e) => {
    const fieldName = `question_${question.id}_term_${termIndex}`;
    setFieldValue(fieldName, e.target.checked);
    setFieldTouched(fieldName, true);
  };

  const toggleExpanded = termIndex => {
    setExpandedTerms(prev => ({
      ...prev,
      [termIndex]: !prev[termIndex],
    }));
  };

  const truncateLabel = (label, termIndex) => {
    const MAX_LENGTH = 122;
    if (label.length <= MAX_LENGTH) {
      return label;
    }

    const isExpanded = expandedTerms[termIndex];
    if (isExpanded) {
      return label;
    }

    return `${label.substring(0, MAX_LENGTH)}...`;
  };

  const getFieldName = termIndex => `question_${question.id}_term_${termIndex}`;

  return (
    <div className="terms-and-conditions">
      <div className="terms-and-conditions__header">
        {question.title && (
          <h2 className="terms-and-conditions__title">{question.title}</h2>
        )}
        {question.description && (
          <div className="terms-and-conditions__description">
            <p>{question.description}</p>
          </div>
        )}
      </div>

      <div className="terms-and-conditions__terms">
        {question.terms &&
          question.terms.map((term, index) => {
            const fieldName = getFieldName(index);
            const isChecked = values[fieldName] || false;
            const fieldError = errors[fieldName];

            const isExpanded = expandedTerms[index];
            const shouldTruncate = term.label.length > 122;

            return (
              <div
                key={`term-${index}`}
                className="terms-and-conditions__term-item"
              >
                <div className="terms-and-conditions__term-checkbox">
                  <Checkbox
                    id={`${fieldName}`}
                    name={fieldName}
                    value={`term-${index}`}
                    label={
                      <span>
                        {term.required && (
                          <span className="terms-and-conditions__required">
                            *
                          </span>
                        )}
                        {truncateLabel(term.label, index)}
                        {shouldTruncate && (
                          <button
                            type="button"
                            className="terms-and-conditions__toggle-btn"
                            onClick={() => toggleExpanded(index)}
                          >
                            {isExpanded
                              ? t("survey:wizard.less")
                              : t("survey:wizard.more")}
                          </button>
                        )}
                      </span>
                    }
                    checked={isChecked}
                    onChange={e => handleCheckboxChange(index, e)}
                  />
                </div>

                {term.pdf_link && (
                  <a
                    href={term.pdf_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-and-conditions__pdf-link"
                  >
                    <svg
                      className="terms-and-conditions__pdf-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4.66675 6.66667L8.00008 10L11.3334 6.66667"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 10V2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {t("survey:wizard.showDocument")}
                  </a>
                )}

                {fieldError && (
                  <div className="terms-and-conditions__error">
                    {fieldError}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

TermsAndConditions.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    terms: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        pdf_link: PropTypes.string,
        required: PropTypes.bool,
      })
    ).isRequired,
  }).isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
};

export default TermsAndConditions;
